import type { MemorizationMode } from '@/types/memorization';
import { cn } from '@/lib/utils/cn';

/**
 * Four-way mode selector for the memorization workflow.
 *
 *   Read    — full visibility, audio available
 *   Repeat  — audio prominent, full visibility (parent decides what
 *             "prominent" looks like via its own layout)
 *   Hide    — Arabic hidden until reveal; audio still works
 *   Recall  — everything hidden until reveal; audio still works
 *
 * The "memorized" state is an action ("Mark as Memorized") not a mode,
 * so it's not represented here. See AyahDisplay + MemorizationView.
 */

type ModeToggleProps = {
  value: MemorizationMode;
  onChange: (mode: MemorizationMode) => void;
};

const OPTIONS: { value: MemorizationMode; label: string; hint: string }[] = [
  { value: 'read', label: 'Read', hint: 'Everything visible' },
  { value: 'repeat', label: 'Repeat', hint: 'Focus on the recitation' },
  { value: 'hide', label: 'Hide', hint: 'Arabic hidden, listen to memorize' },
  { value: 'recall', label: 'Recall', hint: 'Reveal to verify your recall' },
];

export function ModeToggle({ value, onChange }: ModeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Memorization mode"
      className="grid grid-cols-2 sm:grid-cols-4 gap-2"
    >
      {OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(opt.value)}
            title={opt.hint}
            className={cn(
              'group flex flex-col items-center justify-center',
              'h-14 px-3 rounded-xl text-sm font-medium',
              'border transition-colors',
              active
                ? 'bg-accent/10 dark:bg-accent/20 border-accent text-accent-700 dark:text-accent-300'
                : 'bg-white dark:bg-ink-900 border-ink-200 dark:border-ink-800 text-ink-700 dark:text-ink-200 hover:bg-ink-100 dark:hover:bg-ink-800',
            )}
          >
            <span>{opt.label}</span>
            <span
              className={cn(
                'text-[10px] uppercase tracking-wide mt-0.5',
                active
                  ? 'text-accent-600/80 dark:text-accent-400/80'
                  : 'text-ink-400 dark:text-ink-500',
              )}
            >
              {opt.hint.split(',')[0]}
            </span>
          </button>
        );
      })}
    </div>
  );
}
